import type { SkimLevel } from '@/store/useUISettingsStore'

import type { ScrollbarMarker } from '../shared/custom-scrollbar'
import type { KeySentence } from './book-ai-position'
import { PURPOSE_MARK_COLOR, SKIM_LEVEL_STYLES } from './skim-highlight'

type Params = {
  /** 読む目的に関係する本文の断片の位置キー */
  purposeKeys: number[]
  /** 流し読みで強調する重要文（流し読みを使わないときは空） */
  sentences: Pick<KeySentence, 'key' | 'level'>[]
  /** 位置キーをシークバー上の値にする（位置が分からないときは null） */
  toValue: (key: number) => number | null
}

const EMPHASIS = { 1: 'strong', 2: 'normal', 3: 'weak' } as const satisfies Record<SkimLevel, string>

/**
 * シークバーに付ける、AI の印を作る
 * 読む目的の箇所は紫、重要文は同じ位置で最も重要な文の重要度で色と強弱を付ける
 */
export const buildAiMarkers = ({ purposeKeys, sentences, toValue }: Params): ScrollbarMarker[] | undefined => {
  const purposeValues = new Set<number>()
  for (const key of purposeKeys) {
    const value = toValue(key)
    if (value != null) purposeValues.add(value)
  }
  const markers: ScrollbarMarker[] = [...purposeValues].map((value) => ({
    value,
    emphasis: 'normal',
    color: PURPOSE_MARK_COLOR,
  }))

  const levels = new Map<number, SkimLevel>()
  for (const sentence of sentences) {
    const value = toValue(sentence.key)
    if (value == null) continue
    const current = levels.get(value)
    if (current == null || sentence.level < current) levels.set(value, sentence.level)
  }
  for (const [value, level] of levels) {
    markers.push({ value, emphasis: EMPHASIS[level], color: SKIM_LEVEL_STYLES[level].color })
  }
  return markers.length > 0 ? markers : undefined
}
